import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  Drawer,
  Chip,
  Badge,
  Divider,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Pagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Refresh,
  ViewList,
  ViewModule,
  Inventory,
  Category,
  Scale,
  AttachMoney,
  Warning,
  CheckCircle,
  Edit,
  Delete,
  Visibility,
  Close,
  Save,
  Cancel,
  Info,
  MoreVert,
  LocalOffer,
  Storage,
  Diamond,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getProducts, createProduct, updateProduct, deleteProduct, getLowStockProducts } from '../redux/actions/productActions';

const ProductMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { products, lowStockProducts, pagination, loading, error } = useSelector((state) => state.products);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    item_code: '',
    name: '',
    category: '',
    purity: '',
    hsn_code: '',
    huid: '',
    weight: '',
    price_per_gram: '',
    stock_qty: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const categories = ['Gold', 'Silver', 'Diamond', 'Imitation', 'Other'];

  useEffect(() => {
    dispatch(getProducts({ page: currentPage, limit: 10, search: searchTerm, category: categoryFilter }));
    dispatch(getLowStockProducts(10));
  }, [dispatch, searchTerm, categoryFilter, currentPage]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    setSelectedProduct(null);
    setIsEditing(true);
    setFormData({
      item_code: '',
      name: '',
      category: '',
      purity: '',
      hsn_code: '',
      huid: '',
      weight: '',
      price_per_gram: '',
      stock_qty: '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditing(true);
    setFormData({
      item_code: product.item_code || '',
      name: product.name || '',
      category: product.category || '',
      purity: product.purity || '',
      hsn_code: product.hsn_code || '',
      huid: product.huid || '',
      weight: product.weight || '',
      price_per_gram: product.price_per_gram || '',
      stock_qty: product.stock_qty || '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setIsEditing(false);
    setFormData({
      item_code: product.item_code || '',
      name: product.name || '',
      category: product.category || '',
      purity: product.purity || '',
      hsn_code: product.hsn_code || '',
      huid: product.huid || '',
      weight: product.weight || '',
      price_per_gram: product.price_per_gram || '',
      stock_qty: product.stock_qty || '',
    });
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Are you sure you want to delete ${product.name}?`)) {
      const result = await dispatch(deleteProduct(product.id));
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Product deleted successfully',
          severity: 'success'
        });
        dispatch(getProducts({ page: currentPage, limit: 10, search: searchTerm, category: categoryFilter }));
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete product',
          severity: 'error'
        });
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!formData.item_code.trim()) errors.item_code = 'Item code is required';
    if (!formData.name.trim()) errors.name = 'Product name is required';
    if (!formData.category.trim()) errors.category = 'Category is required';
    if (formData.price_per_gram && isNaN(formData.price_per_gram)) {
      errors.price_per_gram = 'Price must be a valid number';
    }
    if (formData.weight && isNaN(formData.weight)) {
      errors.weight = 'Weight must be a valid number';
    }
    if (formData.stock_qty && (isNaN(formData.stock_qty) || formData.stock_qty < 0)) {
      errors.stock_qty = 'Stock quantity must be a valid positive number';
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const result = isEditing && selectedProduct
      ? await dispatch(updateProduct(selectedProduct.id, formData))
      : await dispatch(createProduct(formData));
    
    if (result.success) {
      setSnackbar({
        open: true,
        message: isEditing ? 'Product updated successfully' : 'Product created successfully',
        severity: 'success'
      });
      setFormDrawerOpen(false);
      setSelectedProduct(null);
      setIsEditing(false);
      dispatch(getProducts({ page: currentPage, limit: 10, search: searchTerm, category: categoryFilter }));
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Operation failed',
        severity: 'error'
      });
    }
  };

  const handleFormCancel = () => {
    setFormDrawerOpen(false);
    setSelectedProduct(null);
    setIsEditing(false);
    setFormData({
      item_code: '',
      name: '',
      category: '',
      purity: '',
      hsn_code: '',
      huid: '',
      weight: '',
      price_per_gram: '',
      stock_qty: '',
    });
    setFormErrors({});
  };

  const handleRefresh = () => {
    dispatch(getProducts({ page: currentPage, limit: 10, search: searchTerm, category: categoryFilter }));
  };

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'gold': return <Diamond sx={{ color: '#FFD700' }} />;
      case 'silver': return <Inventory sx={{ color: '#C0C0C0' }} />;
      case 'diamond': return <Diamond sx={{ color: '#B9F2FF' }} />;
      case 'imitation': return <LocalOffer sx={{ color: '#FF6B6B' }} />;
      default: return <Inventory />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'diamond': return '#B9F2FF';
      case 'imitation': return '#FF6B6B';
      default: return 'primary.main';
    }
  };

  const getStockStatus = (stockQty) => {
    if (stockQty <= 5) return { color: 'error', label: 'Low Stock', icon: <Warning /> };
    if (stockQty <= 10) return { color: 'warning', label: 'Medium Stock', icon: <Info /> };
    return { color: 'success', label: 'In Stock', icon: <CheckCircle /> };
  };

  const getStats = () => {
    const total = products.length;
    const lowStock = products.filter(p => p.stock_qty <= 5).length;
    const outOfStock = products.filter(p => p.stock_qty === 0).length;
    const categories = [...new Set(products.map(p => p.category))].length;
    
    return { total, lowStock, outOfStock, categories };
  };

  const stats = getStats();

  const filteredProducts = products.filter(product => {
    if (stockFilter === 'low') return product.stock_qty <= 5;
    if (stockFilter === 'out') return product.stock_qty === 0;
    if (stockFilter === 'in') return product.stock_qty > 5;
    return true;
  });

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Product Master
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your jewellery inventory with advanced features
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{ borderRadius: 2 }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Products
                  </Typography>
                </Box>
                <Inventory sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.lowStock}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Low Stock
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.categories}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Categories
                  </Typography>
                </Box>
                <Category sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.outOfStock}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Out of Stock
                  </Typography>
                </Box>
                <Storage sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Side - Product Grid */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Search and Filter Bar */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 400 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      label="Category"
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Stock</InputLabel>
                    <Select
                      value={stockFilter}
                      label="Stock"
                      onChange={(e) => setStockFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Stock</MenuItem>
                      <MenuItem value="in">In Stock</MenuItem>
                      <MenuItem value="low">Low Stock</MenuItem>
                      <MenuItem value="out">Out of Stock</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {pagination.totalItems} products found
                  </Typography>
                  <Chip
                    label={`Page ${pagination.currentPage} of ${pagination.totalPages}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Product List */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography>Loading...</Typography>
                  </Box>
                ) : filteredProducts.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, p: 3 }}>
                    <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No products found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first product to get started'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell>Product</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>HUID</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Stock</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredProducts.map((product) => {
                          const stockStatus = getStockStatus(product.stock_qty);
                          return (
                            <TableRow 
                              key={product.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                              onClick={() => handleView(product)}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: getCategoryColor(product.category), width: 40, height: 40 }}>
                                    {getCategoryIcon(product.category)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {product.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {product.item_code}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={product.category}
                                  size="small"
                                  sx={{ 
                                    bgcolor: getCategoryColor(product.category),
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {product.huid || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    ₹{product.price_per_gram || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    per gram
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={stockStatus.label}
                                    size="small"
                                    color={stockStatus.color}
                                    variant="outlined"
                                    icon={stockStatus.icon}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {product.stock_qty}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(product);
                                      }}
                                      color="primary"
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(product);
                                      }}
                                      color="error"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Pagination */}
              {!loading && filteredProducts.length > 0 && pagination.totalPages > 1 && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Product Form */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {isEditing ? 'Edit Product' : selectedProduct ? 'Product Details' : 'Add New Product'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isEditing ? 'Update product information' : selectedProduct ? 'View product details' : 'Enter product information'}
                    </Typography>
                  </Box>
                  {selectedProduct && (
                    <IconButton onClick={handleFormCancel} size="small">
                      <Close />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleFormSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Item Code"
                        name="item_code"
                        value={formData.item_code}
                        onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                        error={!!formErrors.item_code}
                        helperText={formErrors.item_code}
                        disabled={!isEditing && selectedProduct}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Inventory color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Product Name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        disabled={!isEditing && selectedProduct}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Inventory color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Category</InputLabel>
                        <Select
                          name="category"
                          value={formData.category}
                          label="Category"
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          disabled={!isEditing && selectedProduct}
                        >
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getCategoryIcon(category)}
                                {category}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Purity"
                        name="purity"
                        value={formData.purity}
                        onChange={(e) => setFormData({ ...formData, purity: e.target.value })}
                        disabled={!isEditing && selectedProduct}
                        placeholder="e.g., 22K, 18K, 925"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Diamond color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="HSN Code (Optional)"
                        name="hsn_code"
                        value={formData.hsn_code}
                        onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })}
                        disabled={!isEditing && selectedProduct}
                        placeholder="e.g., 7113, 7114 (optional)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Category color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="HUID (Optional)"
                        name="huid"
                        value={formData.huid}
                        onChange={(e) => setFormData({ ...formData, huid: e.target.value })}
                        disabled={!isEditing && selectedProduct}
                        placeholder="Enter HUID (optional)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Inventory color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Weight (grams)"
                        name="weight"
                        type="number"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        error={!!formErrors.weight}
                        helperText={formErrors.weight}
                        disabled={!isEditing && selectedProduct}
                        inputProps={{ step: "0.001" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Scale color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price per Gram (₹)"
                        name="price_per_gram"
                        type="number"
                        value={formData.price_per_gram}
                        onChange={(e) => setFormData({ ...formData, price_per_gram: e.target.value })}
                        error={!!formErrors.price_per_gram}
                        helperText={formErrors.price_per_gram}
                        disabled={!isEditing && selectedProduct}
                        inputProps={{ step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Stock Quantity"
                        name="stock_qty"
                        type="number"
                        value={formData.stock_qty}
                        onChange={(e) => setFormData({ ...formData, stock_qty: e.target.value })}
                        error={!!formErrors.stock_qty}
                        helperText={formErrors.stock_qty}
                        disabled={!isEditing && selectedProduct}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Storage color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {/* Form Actions */}
              {isEditing && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleFormCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleFormSubmit}
                    >
                      {selectedProduct ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </Box>
              )}

              {!isEditing && selectedProduct && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(selectedProduct)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mobile Form Drawer */}
      <Drawer
        anchor="bottom"
        open={formDrawerOpen && isMobile}
        onClose={handleFormCancel}
        sx={{
          '& .MuiDrawer-paper': {
            height: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {isEditing ? 'Edit Product' : selectedProduct ? 'Product Details' : 'Add New Product'}
            </Typography>
            <IconButton onClick={handleFormCancel}>
              <Close />
            </IconButton>
          </Box>
          {/* Mobile form content would go here */}
        </Box>
      </Drawer>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddNew}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductMaster;